import {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Skeleton,
    IconButton,
    Stack,
    Checkbox,
    FormControlLabel,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    AuditCertificateService,
    AuditCertificateBO,
} from '@/services/manageService/auditCertificateService';
import {InputField} from '@/components/common';

interface Props {
    open: boolean;
    onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
    certificateId?: number;
}

const EMPTY_FORM = {
    auditorEnglishName: '',
    accountingFirmName: '',
    accountingFirmChineseName: '',
    auditorSignName: '',
    pcNumber: '',
    auditorAddress: '',
    auditorChineseAddress: '',
    auditorFaxNumber: '',
    auditorTelRepNumber: '',
    auditorTelBankConfNumber: '',
    auditorBrNumber: '',
    certificateUrl: '',
    isValid: 1,
};

export default function AuditCertificateDetail({open, onClose, certificateId}: Props) {
    const isEdit = !!certificateId;
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState<{ auditorEnglishName?: string; _global?: string }>({});

    const loadDetail = async () => {
        if (!certificateId) return;
        try {
            setLoading(true);
            const data = await AuditCertificateService.getById(certificateId);
            setForm({
                auditorEnglishName: data.auditorEnglishName ?? '',
                accountingFirmName: data.accountingFirmName ?? '',
                accountingFirmChineseName: data.accountingFirmChineseName ?? '',
                auditorSignName: data.auditorSignName ?? '',
                pcNumber: data.pcNumber ?? '',
                auditorAddress: data.auditorAddress ?? '',
                auditorChineseAddress: data.auditorChineseAddress ?? '',
                auditorFaxNumber: data.auditorFaxNumber ?? '',
                auditorTelRepNumber: data.auditorTelRepNumber ?? '',
                auditorTelBankConfNumber: data.auditorTelBankConfNumber ?? '',
                auditorBrNumber: data.auditorBrNumber ?? '',
                certificateUrl: data.certificateUrl ?? '',
                isValid: data.isValid ?? 1,
            });
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEdit) {
                loadDetail();
            } else {
                setForm(EMPTY_FORM);
            }
            setFieldErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, certificateId]);

    const validate = (): boolean => {
        const errors: typeof fieldErrors = {};
        if (!form.auditorEnglishName.trim()) errors.auditorEnglishName = 'Auditor English Name is required';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildBo = (): AuditCertificateBO => ({
        auditorEnglishName: form.auditorEnglishName.trim(),
        accountingFirmName: form.accountingFirmName,
        accountingFirmChineseName: form.accountingFirmChineseName,
        auditorSignName: form.auditorSignName,
        pcNumber: form.pcNumber,
        auditorAddress: form.auditorAddress,
        auditorChineseAddress: form.auditorChineseAddress,
        auditorFaxNumber: form.auditorFaxNumber,
        auditorTelRepNumber: form.auditorTelRepNumber,
        auditorTelBankConfNumber: form.auditorTelBankConfNumber,
        auditorBrNumber: form.auditorBrNumber,
        certificateUrl: form.certificateUrl,
        isValid: form.isValid,
    });

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const bo = buildBo();
            if (isEdit && certificateId) {
                await AuditCertificateService.update(certificateId, bo);
            } else {
                await AuditCertificateService.create(bo);
            }
            onClose({refreshed: true, message: 'Saved successfully', severity: 'success'});
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Save failed';
            setFieldErrors({_global: message});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEdit ? 'Edit Audit Certification' : 'Create Audit Certification'}
                    <IconButton size="small" onClick={() => onClose()}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Stack spacing={2} mt={1}>
                        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={40}/>)}
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={1}>
                        <InputField size="small" label="Auditor English Name" required
                                    error={!!fieldErrors.auditorEnglishName}
                                    helperText={fieldErrors.auditorEnglishName}
                                    value={form.auditorEnglishName}
                                    onChange={(e) => setForm({...form, auditorEnglishName: e.target.value})}/>
                        <InputField size="small" label="Accounting Firm (EN)"
                                    value={form.accountingFirmName}
                                    onChange={(e) => setForm({...form, accountingFirmName: e.target.value})}/>
                        <InputField size="small" label="Accounting Firm (CN)"
                                    value={form.accountingFirmChineseName}
                                    onChange={(e) => setForm({...form, accountingFirmChineseName: e.target.value})}/>
                        <InputField size="small" label="Auditor Sign Name"
                                    value={form.auditorSignName}
                                    onChange={(e) => setForm({...form, auditorSignName: e.target.value})}/>
                        <InputField size="small" label="PC Number"
                                    value={form.pcNumber}
                                    onChange={(e) => setForm({...form, pcNumber: e.target.value})}/>
                        <InputField size="small" label="Auditor Address (EN)"
                                    value={form.auditorAddress}
                                    onChange={(e) => setForm({...form, auditorAddress: e.target.value})}/>
                        <InputField size="small" label="Auditor Address (CN)"
                                    value={form.auditorChineseAddress}
                                    onChange={(e) => setForm({...form, auditorChineseAddress: e.target.value})}/>
                        <InputField size="small" label="Fax Number"
                                    value={form.auditorFaxNumber}
                                    onChange={(e) => setForm({...form, auditorFaxNumber: e.target.value})}/>
                        <InputField size="small" label="Tel (Rep Letter)"
                                    value={form.auditorTelRepNumber}
                                    onChange={(e) => setForm({...form, auditorTelRepNumber: e.target.value})}/>
                        <InputField size="small" label="Tel (Bank Confirmation)"
                                    value={form.auditorTelBankConfNumber}
                                    onChange={(e) => setForm({...form, auditorTelBankConfNumber: e.target.value})}/>
                        <InputField size="small" label="BR Number"
                                    value={form.auditorBrNumber}
                                    onChange={(e) => setForm({...form, auditorBrNumber: e.target.value})}/>
                        <InputField size="small" label="Certificate URL"
                                    value={form.certificateUrl}
                                    onChange={(e) => setForm({...form, certificateUrl: e.target.value})}/>
                        <FormControlLabel
                            control={
                                <Checkbox size="small" checked={form.isValid === 1}
                                          onChange={(e) => setForm({...form, isValid: e.target.checked ? 1 : 0})}/>
                            }
                            label="Valid"
                        />
                        {fieldErrors._global && (
                            <FormHelperText error>{fieldErrors._global}</FormHelperText>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
